import { type FormEvent, useId, useState } from 'react';
import styled from 'styled-components';
import BulkTableModal from './BulkTableModal';

export type ListCompletionMode = 'table' | 'web';

const ChoiceGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 20px;
  margin: 20px 0 24px;

  @media (max-width: 700px) {
    grid-template-columns: 1fr;
  }
`;

const ChoiceCard = styled.label<{ $selected: boolean }>`
  display: flex;
  min-width: 0;
  padding: 20px;
  border: 3px solid ${({ $selected }) => ($selected ? '#4c2c92' : '#b1b4b6')};
  background: ${({ $selected }) => ($selected ? '#f4f0f7' : '#ffffff')};
  box-shadow: ${({ $selected }) => ($selected ? 'inset 0 0 0 1px #4c2c92' : 'none')};
  cursor: pointer;

  &:hover {
    border-color: #4c2c92;
  }

  &:focus-within {
    outline: 3px solid #ffdd00;
    outline-offset: 0;
    box-shadow: 0 0 0 5px #0b0c0c;
  }
`;

const ChoiceInput = styled.input`
  flex: 0 0 auto;
  width: 28px;
  height: 28px;
  margin: 1px 14px 0 0;
  accent-color: #1d70b8;
  cursor: pointer;
`;

const ChoiceContent = styled.span`
  display: flex;
  min-width: 0;
  flex: 1;
  flex-direction: column;
`;

const ChoiceHeading = styled.span`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;

  @media (max-width: 700px) {
    flex-direction: column;
    gap: 6px;
  }
`;

const SelectedBadge = styled.span`
  flex: 0 0 auto;
  padding: 3px 8px;
  border-radius: 2px;
  color: #ffffff;
  background: #4c2c92;
  font-size: 0.875rem;
  font-weight: 700;
  line-height: 1.2;
`;

const Illustration = styled.svg`
  width: 100%;
  height: auto;
  margin: 14px 0;
  border: 1px solid #b1b4b6;
  background: #ffffff;
`;

const TableIllustration = () => (
  <Illustration aria-hidden="true" focusable="false" viewBox="0 0 320 150">
    <rect width="320" height="150" fill="#f3f2f1" />
    <rect x="18" y="18" width="284" height="28" fill="#1d70b8" />
    {[46, 76, 106].map((y) => (
      <g key={y}>
        <rect x="18" y={y} width="284" height="30" fill="#ffffff" />
        <path d={`M18 ${y}H302M18 ${y + 30}H302`} stroke="#b1b4b6" />
      </g>
    ))}
    {[18, 89, 160, 231, 302].map((x) => (
      <path key={x} d={`M${x} 18V136`} stroke="#b1b4b6" />
    ))}
    <rect x="90" y="77" width="69" height="28" fill="#fff7bf" stroke="#0b0c0c" strokeWidth="2" />
    <path d="M101 91h47" stroke="#505a5f" strokeWidth="4" />
  </Illustration>
);

const WebFormIllustration = () => (
  <Illustration aria-hidden="true" focusable="false" viewBox="0 0 320 150">
    <rect width="320" height="150" fill="#f3f2f1" />
    <rect x="42" y="14" width="236" height="122" fill="#ffffff" />
    <path d="M60 35h126" stroke="#0b0c0c" strokeWidth="8" />
    <path d="M60 53h180" stroke="#505a5f" strokeWidth="4" />
    <rect x="60" y="67" width="196" height="32" fill="#ffffff" stroke="#0b0c0c" strokeWidth="3" />
    <rect x="60" y="110" width="88" height="16" fill="#00703c" />
  </Illustration>
);

type Props = {
  currentMode?: ListCompletionMode;
  onCancel: () => void;
  onContinue: (mode: ListCompletionMode) => void;
};

export default function ListCompletionModeModal({ currentMode, onCancel, onContinue }: Props) {
  const hintId = useId();
  const errorId = useId();
  const [selectedMode, setSelectedMode] = useState<ListCompletionMode | undefined>(currentMode);
  const [showError, setShowError] = useState(false);

  const handleSubmit = (event: FormEvent) => {
    event.preventDefault();
    if (!selectedMode) {
      setShowError(true);
      return;
    }
    onContinue(selectedMode);
  };

  return (
    <BulkTableModal
      title="How would you like to complete this form?"
      width="min(100%, 900px)"
      zIndex={1030}
      onClose={onCancel}
    >
      <form onSubmit={handleSubmit}>
        <fieldset
          className={`govuk-fieldset${showError ? ' govuk-form-group--error' : ''}`}
          aria-describedby={`${hintId}${showError ? ` ${errorId}` : ''}`}
        >
          <legend className="govuk-visually-hidden">Select an input method</legend>
          <p className="govuk-hint" id={hintId}>
            Select one option
          </p>
          {showError && (
            <p className="govuk-error-message" id={errorId} role="alert">
              <span className="govuk-visually-hidden">Error:</span> Select how you want to complete
              this form
            </p>
          )}
          <ChoiceGrid>
            <ChoiceCard $selected={selectedMode === 'table'}>
              <ChoiceInput
                type="radio"
                name="list-completion-mode"
                value="table"
                checked={selectedMode === 'table'}
                onChange={() => {
                  setSelectedMode('table');
                  setShowError(false);
                }}
              />
              <ChoiceContent>
                <ChoiceHeading>
                  <strong className="govuk-heading-m govuk-!-margin-bottom-0">Table format</strong>
                  {selectedMode === 'table' && (
                    <SelectedBadge aria-hidden="true">Selected</SelectedBadge>
                  )}
                </ChoiceHeading>
                <TableIllustration />
                <span className="govuk-body govuk-!-margin-bottom-0">
                  Enter multiple rows in a table, similar to Microsoft Excel
                </span>
              </ChoiceContent>
            </ChoiceCard>
            <ChoiceCard $selected={selectedMode === 'web'}>
              <ChoiceInput
                type="radio"
                name="list-completion-mode"
                value="web"
                checked={selectedMode === 'web'}
                onChange={() => {
                  setSelectedMode('web');
                  setShowError(false);
                }}
              />
              <ChoiceContent>
                <ChoiceHeading>
                  <strong className="govuk-heading-m govuk-!-margin-bottom-0">Web form</strong>
                  {selectedMode === 'web' && (
                    <SelectedBadge aria-hidden="true">Selected</SelectedBadge>
                  )}
                </ChoiceHeading>
                <WebFormIllustration />
                <span className="govuk-body govuk-!-margin-bottom-0">
                  Enter one row at a time in a guided form - slower, but often easier with assistive
                  technology
                </span>
              </ChoiceContent>
            </ChoiceCard>
          </ChoiceGrid>
        </fieldset>
        <p className="govuk-body">You can change this later.</p>
        <div className="govuk-button-group">
          <button className="govuk-button" type="submit" data-module="govuk-button">
            Continue
          </button>
          <button
            className="govuk-button govuk-button--secondary"
            type="button"
            data-module="govuk-button"
            onClick={onCancel}
          >
            Cancel
          </button>
        </div>
      </form>
    </BulkTableModal>
  );
}
